import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, MoreHorizontal, Edit, Trash2, Reply,
  Send, Clock, Flag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const CommentThread = ({ comment, postId, onCommentUpdate, level = 0 }) => {
  const profile = useSelector(state => state.profile);
  const [showReplies, setShowReplies] = useState(true);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLiked, setIsLiked] = useState(comment.isLikedByUser);
  const [likeCount, setLikeCount] = useState(comment.likeCount || 0);

  const maxLevel = 1;
  const canReply = level < maxLevel;

  const formatTimeAgo = (date) => {
    const now = new Date();
    const commentDate = new Date(date);
    const diffInHours = Math.floor((now - commentDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'now';
    if (diffInHours < 24) return `${diffInHours}h`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d`;
    return commentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/forumV3/comments/${commentId}/like`,
        { alumniId: profile._id }
      );
      
      if (response.data.success) {
        setIsLiked(response.data.isLiked);
        setLikeCount(response.data.likeCount);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to like comment');
    }
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !canReply) return;

    setSubmittingReply(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}/comments`,
        {
          content: replyText.trim(),
          parentCommentId: comment._id, 
          alumniId: profile._id
        }
      );
      
      if (response.data.success) {
        setReplyText('');
        setShowReplyForm(false);
        onCommentUpdate();
         toast.dismiss() ;  toast.success('Reply added successfully');
      }
    } catch (error) {
      console.error('Error submitting reply:', error);
      toast.error('Failed to add reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    const hasReplies = comment.replies && comment.replies.length > 0;
    const deleteMessage = hasReplies 
      ? `Delete this comment and all ${comment.replies.length} replies? This action cannot be undone.`
      : 'Delete this comment? This action cannot be undone.';
    
    if (!window.confirm(deleteMessage)) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/forumV3/comments/${commentId}`
      );
      
      if (response.data.success) {
        onCommentUpdate();
         toast.dismiss() ;  toast.success(response.data.message || 'Comment deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const isAuthor = comment?.alumniId?._id === profile?._id;
  const isAdmin = profile.admin;

  return (
    <div className={`p-3 lg:p-4 ${level > 0 ? 'ml-4 lg:ml-6 border-l-2 border-gray-200 pl-3 lg:pl-4' : ''}`}>
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
          {comment?.alumniId?.profilePicture ? (
            <img
              src={comment?.alumniId?.profilePicture}
              alt={`${comment?.alumniId?.firstName} ${comment?.alumniId?.lastName}`}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full dynamic-site-bg flex items-center justify-center text-white font-medium text-xs lg:text-sm">
              {comment?.alumniId?.firstName?.[0]}{comment?.alumniId?.lastName?.[0]}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-sm text-gray-900">
                {comment?.alumniId?.firstName} {comment?.alumniId?.lastName}
              </h4>
              {comment?.alumniId?.graduation_year && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  '{comment?.alumniId?.graduation_year.toString().slice(-2)}
                </span>
              )}
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Clock size={10} />
                {formatTimeAgo(comment.createdAt)}
              </span>
              {comment.isEdited && (
                <span className="text-xs text-gray-500">(edited)</span>
              )}
            </div>

            {/* Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="w-6 h-6 rounded-lg hover:bg-gray-100 flex items-center justify-center"
              >
                <MoreHorizontal size={14} className="text-gray-500" />
              </button>

              {showDropdown && (
                <>
                  <div className="absolute right-0 top-6 w-32 bg-white rounded-lg shadow-lg border py-1 z-20">
                    {(isAuthor || isAdmin) && (
                      <button
                        onClick={() => {
                          setShowDropdown(false);
                          handleDeleteComment(comment._id);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 size={12} />
                        Delete
                      </button>
                    )}

                    {!isAuthor && (
                      <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <Flag size={12} />
                        Report
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="text-gray-700 text-sm mb-3 leading-relaxed">
            {comment?.content?.split('\n').map((line, index) => (
              <p key={index} className="mb-1 last:mb-0">
                {line}
              </p>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 mb-3">
            <button
              onClick={() => handleLikeComment(comment._id)}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg font-medium transition-colors ${
                isLiked
                  ? 'text-red-600 bg-red-50'
                  : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
              }`}
            >
              <Heart size={12} className={isLiked ? 'fill-current' : ''} />
              <span>{likeCount}</span>
            </button>

            {canReply && (
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="flex items-center gap-1 text-xs text-gray-600 hover:text-blue-600 px-2 py-1 rounded-lg hover:bg-blue-50 transition-colors font-medium"
              >
                <Reply size={12} />
                Reply
              </button>
            )}

            {comment.replies && comment.replies.length > 0 && (
              <button
                onClick={() => setShowReplies(!showReplies)}
                className="text-xs text-gray-600 hover:text-blue-600 font-medium"
              >
                {showReplies ? 'Hide' : 'Show'} {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
              </button>
            )}
          </div>

          {/* Reply Form */}
          <AnimatePresence>
            {showReplyForm && canReply && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                onSubmit={handleSubmitReply}
                className="mb-4 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                    {profile.profilePicture ? (
                      <img
                        src={profile.profilePicture}
                        alt="Your profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full dynamic-site-bg flex items-center justify-center text-white text-xs font-medium">
                        {profile.firstName?.[0]}{profile.lastName?.[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={`Reply to ${comment?.alumniId?.firstName}...`}
                      rows={2}
                      className="w-full px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-sm"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowReplyForm(false);
                          setReplyText('');
                        }}
                        className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!replyText.trim() || submittingReply}
                        className="flex items-center gap-1 px-2 py-1 dynamic-site-bg text-white rounded-lg text-xs font-medium hover:opacity-90 disabled:opacity-50"
                      >
                        {submittingReply ? (
                          <div className="w-3 h-3 border border-white/20 border-t-white rounded-full animate-spin" />
                        ) : (
                          <Send size={10} />
                        )}
                        {submittingReply ? 'Posting...' : 'Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Replies */}
          <AnimatePresence>
            {showReplies && comment.replies && comment.replies.length > 0 && level < maxLevel && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                {comment.replies.map((reply) => (
                  <CommentThread
                    key={reply._id}
                    comment={reply}
                    postId={postId}
                    onCommentUpdate={onCommentUpdate}
                    level={level + 1}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CommentThread;
